function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

import { klona as deepClone } from "klona";
import deepMerge from "deepmerge";
import { detailedDiff } from "deep-object-diff";
import useTheme from "./useTheme";
import { DEFAULT_BREAKPOINT } from "../utils/css";
import { hasOwnProperty, isObjectEmpty } from "../utils/core";

function getResponsiveProp(prop, breakpoints) {
  for (var i = 0; i < breakpoints.length; i++) {
    var responsiveSuffix = "-".concat(breakpoints[i]);

    if (prop.endsWith(responsiveSuffix)) {
      return prop.slice(0, -responsiveSuffix.length);
    }
  }

  return prop;
}

function getResponsiveProps(props, breakpoints) {
  var result = {};

  for (var prop in props) {
    result[getResponsiveProp(prop, breakpoints)] = true;
  }

  return result;
}

function hasAnyBreakpoint(prop, breakpoints) {
  return breakpoints.some(function (bp) {
    return prop.endsWith("-".concat(bp));
  });
}

function getDefaultBreakpointProps(props, breakpoints) {
  var result = _objectSpread({}, props);

  for (var prop in result) {
    if (hasAnyBreakpoint(prop, breakpoints)) {
      delete result[prop];
    }
  }

  return result;
}

export function getBreakpointToPropsMap(theme, props, defaultProps) {
  var breakpoints = Object.keys(theme.breakpoints);
  /*
    If `props` is { "height-sm": "40px" }, `getResponsiveProps` will include "height".
    But, `getDefaultBreakpointProps` will not.
  */

  var nonBreakpointProps = getResponsiveProps(props, breakpoints);
  var lastBreakpointProps = getDefaultBreakpointProps(props, breakpoints);

  var result = _defineProperty({}, DEFAULT_BREAKPOINT, _objectSpread(_objectSpread({}, defaultProps), lastBreakpointProps));

  breakpoints.forEach(function (bp) {
    result[bp] = _objectSpread(_objectSpread({}, defaultProps), lastBreakpointProps);

    for (var prop in nonBreakpointProps) {
      var propAtBreakpoint = "".concat(prop, "-").concat(bp);

      if (hasOwnProperty(props, propAtBreakpoint)) {
        result[bp][prop] = props[propAtBreakpoint];
      }
    }

    lastBreakpointProps = result[bp];
  });
  return result;
}

function getCSSforBreakpoint(responsiveProps, propsAtBreakpoint, theme, bp) {
  var result = {};

  for (var prop in responsiveProps) {
    result = _objectSpread(_objectSpread({}, result), responsiveProps[prop](propsAtBreakpoint, theme, bp));
  }

  return result;
}

function removeRedundantCSS(newCSS, existingCSS, mediaQueries) {
  var remainingNewCSS = deepClone(newCSS);
  var result = {};

  for (var i = mediaQueries.length - 1; i >= 0 && !isObjectEmpty(remainingNewCSS); i--) {
    var breakpointCSS = existingCSS[mediaQueries[i]] || {};

    var _detailedDiff = detailedDiff(breakpointCSS, remainingNewCSS),
        added = _detailedDiff.added,
        updated = _detailedDiff.updated; // The order is important here


    result = deepMerge(result, updated);
    remainingNewCSS = added;
  }

  result = deepMerge(result, remainingNewCSS);
  return result;
}

var DEFAULT_BREAKPOINT_MEDIA_QUERY_PLACEHOLDER = "";

function useResponsivePropsCSS(props, defaultProps, responsiveProps) {
  var theme = useTheme();
  var breakpointToPropsMap = getBreakpointToPropsMap(theme, props, defaultProps);
  var breakpoints = [DEFAULT_BREAKPOINT].concat(Object.keys(theme.minMediaQueries));
  var result = {};

  for (var i = 0; i < breakpoints.length; i++) {
    var bp = breakpoints[i];
    var newCSS = getCSSforBreakpoint(responsiveProps, breakpointToPropsMap[bp], theme, bp);
    var necessaryNewCSS = removeRedundantCSS(newCSS, result, breakpoints.slice(0, i).map(function (bp) {
      return theme.minMediaQueries[bp] || DEFAULT_BREAKPOINT_MEDIA_QUERY_PLACEHOLDER;
    }));

    if (!isObjectEmpty(necessaryNewCSS)) {
      result[theme.minMediaQueries[bp] || DEFAULT_BREAKPOINT_MEDIA_QUERY_PLACEHOLDER] = necessaryNewCSS;
    }
  }
  /*
    Note: The merge order is important here!
          Media queries should come after the default CSS.
          Also, the media queries should be in order.
     If the default CSS was placed after the media queries, e.g.:
      
      {
        "@media (min-width: 576px)": {
          height: "24px"
        },
        height: "48px"
      }
     the media query definition would be overridden by Order of Appearance (https://www.w3.org/TR/css-cascade-3/#cascade-order).
     Is the media queries were is the wrong order, e.g.:
       {
        "@media (min-width: 576px)": {
          height: "24px"
        },
        "@media (min-width: 375px)": {
          height: "48px"
        }
      }
     on screen sizes >= 576px, the height would be 48px, which is wrong.
  */


  result = _objectSpread(_objectSpread({}, result[DEFAULT_BREAKPOINT_MEDIA_QUERY_PLACEHOLDER]), result);
  delete result[DEFAULT_BREAKPOINT_MEDIA_QUERY_PLACEHOLDER];
  return result;
}

export default useResponsivePropsCSS;